import packageJson from "../../package.json";
import {
  APP_VERSIONS_COLLECTION,
  USERS_COLLECTION,
} from "../constants/collections";
import { auth } from "../firebase";
import { Alert, Platform } from "react-native";
import * as Updates from "expo-updates";
import { getDocument, setDocument, update } from "./firestoreServices";
import i18n from "../i18n";

const VERSION_CHECK_INTERVAL = __DEV__ ? 1000 * 60 * 10 : 10 * 60 * 1000; // Check every 5 minutes

interface AppVersion {
  version: string;
  domain: string;
  lastDeployDate: string;
  updatedAt: any;
  id?: string;
}

const getCurrentDomain = (): string => {
  if (Platform.OS === "web") {
    return window.location.hostname;
  }
  // For mobile apps, you might want to use a different identifier
  // For example, you could use the app's bundle identifier
  return Platform.OS === "ios" ? "ios" : "android";
};

const updateUserVersion = async (version: string, domain: string) => {
  try {
    const user = auth.currentUser;
    if (!user) return;

    await update(USERS_COLLECTION, user.uid, {
      appVersion: version,
      appDomain: domain,
    });
  } catch (error) {
    console.error("Error updating user version:", error);
  }
};

const initializeVersionData = async (
  currentVersion: string,
  domain: string,
) => {
  try {
    await setDocument(APP_VERSIONS_COLLECTION, domain, {
      version: currentVersion,
      domain: domain,
      lastDeployDate: new Date().toISOString(),
      updatedAt: new Date(),
    });
    await updateUserVersion(currentVersion, domain);
    return true;
  } catch (error) {
    console.error("Error initializing version data:", error);
    return false;
  }
};

const compareVersions = (v1: string, v2: string): number => {
  const v1Parts = v1.split(".").map(Number);
  const v2Parts = v2.split(".").map(Number);

  for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
    const v1Part = v1Parts[i] || 0;
    const v2Part = v2Parts[i] || 0;

    if (v1Part > v2Part) return 1;
    if (v1Part < v2Part) return -1;
  }

  return 0;
};

const checkVersion = async () => {
  try {
    const currentVersion = packageJson.version;
    const currentDomain = getCurrentDomain();

    if (!currentVersion) {
      console.error("No version found in package.json");
      return;
    }

    let versionDoc;
    try {
      versionDoc = await getDocument(APP_VERSIONS_COLLECTION, currentDomain);
    } catch (error) {
      console.error("Error fetching version document:", error);
      // Try to initialize version data if fetch fails
      await initializeVersionData(currentVersion, currentDomain);
      return;
    }

    // If no version document exists, initialize it
    if (!versionDoc) {
      const initialized = await initializeVersionData(
        currentVersion,
        currentDomain,
      );
      if (!initialized) {
        console.error("Failed to initialize version data");
      }
      return;
    }

    const firestoreVersion = versionDoc as unknown as AppVersion;
    if (!firestoreVersion.version) {
      console.error("Invalid version data in Firestore");
      await initializeVersionData(currentVersion, currentDomain);
      return;
    }

    // Update user's version regardless of comparison
    await updateUserVersion(currentVersion, currentDomain);

    // Check if current version is newer than Firestore version
    if (compareVersions(currentVersion, firestoreVersion.version) > 0) {
      await setDocument(APP_VERSIONS_COLLECTION, currentDomain, {
        version: currentVersion,
        domain: currentDomain,
        lastDeployDate: new Date().toISOString(),
        updatedAt: new Date(),
      });
      return;
    }

    // Check if Firestore version is newer than current version
    if (compareVersions(firestoreVersion.version, currentVersion) > 0) {
      if (Platform.OS === "web") {
        const shouldUpdate = window.confirm(
          i18n
            .t(
              "A new version ({{version}}) is available. Would you like to update now?",
              {
                version: firestoreVersion.version,
              },
            )
            .toString(),
        );
        if (shouldUpdate) {
          window.location.reload();
        }
      } else {
        Alert.alert(
          i18n.t("Update Available"),
          i18n
            .t(
              "A new version ({{version}}) is available. Would you like to update now?",
              {
                version: firestoreVersion.version,
              },
            )
            .toString(),
          [
            {
              text: i18n.t("Later"),
              style: "cancel",
            },
            {
              text: i18n.t("Update"),
              onPress: async () => {
                try {
                  await Updates.reloadAsync();
                } catch (error) {
                  console.error("Error reloading app:", error);
                }
              },
            },
          ],
        );
      }
    }
  } catch (error) {
    console.error("Error in version check:", error);
    // Try to initialize version data if check fails
    try {
      const currentDomain = getCurrentDomain();
      await initializeVersionData(packageJson.version, currentDomain);
    } catch (initError) {
      console.error(
        "Failed to initialize version data after error:",
        initError,
      );
    }
  }
};

let versionCheckInterval: NodeJS.Timeout | null = null;

export const startVersionCheck = () => {
  // Clear any existing interval
  if (versionCheckInterval) {
    clearInterval(versionCheckInterval);
  }

  // Initial check
  checkVersion();

  // Set up periodic checks
  versionCheckInterval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);
};

export const stopVersionCheck = () => {
  if (versionCheckInterval) {
    clearInterval(versionCheckInterval);
    versionCheckInterval = null;
  }
};
